import { Injectable } from '@angular/core';
import { ReservationService } from '../reservation/reservation.service';
import { SessionService } from '../../../core/session.service';
import { HttpClient } from '@angular/common/http';
import { SessionData } from '../../../core/session-data';
import { DriverInformation, Reservation } from '../reservation/reservation';
import { QuoteRequest, QuoteResponse, Traveler, Trip } from './quote';
import { BehaviorSubject } from 'rxjs';
import { QuoteStatus } from './insurance-status.enum';
import { SilentError } from '../../../core/error';

@Injectable({
  providedIn: 'root'
})
export class QuoteService {
 
  private sessionData: SessionData;
  private reservation: Reservation;
  private quoteResponse$ = new BehaviorSubject<QuoteResponse>(undefined);
  private quoteStatus = QuoteStatus.Pending;

  constructor(
    private reservationService: ReservationService,
    private sessionService: SessionService,
    private httpClient: HttpClient
  ) {
    this.sessionData = this.sessionService.getSessionData();
  }

  public makeQuoteRequest() {
    this.setReservationData();
    this.sendQuoteRequest();
  }

  public getDailyPrice(): number {
    if (this.quoteStatus === QuoteStatus.Successful)
      return this.quoteResponse$.value.price / this.getDays();
    return 0;
  }

  public getQuotePrice(): number {
    if (this.quoteStatus === QuoteStatus.Successful)
      return this.quoteResponse$.value.price;
    return 0;
  }

  public getQuote(): QuoteResponse {
    return this.quoteResponse$.value;
  }

  public resetQuote() {
    this.quoteResponse$.next(undefined);
  }

  public getQuoteStatus(): QuoteStatus {
    return this.quoteStatus;
  }

  public setInvalidStatus() {
    this.quoteStatus = QuoteStatus.Invalid;
  }

  public setLoadingStatus() {
    this.quoteStatus = QuoteStatus.Loading;
  }

  public setPendingStatus() {
    this.quoteStatus = QuoteStatus.Pending;
  }

  public clearQuote() {
    this.resetQuote();
    this.setPendingStatus();
  }

  private setReservationData() {
    this.reservation = this.reservationService.reservation;
  }

  private sendQuoteRequest() {
    const request = this.buildQuoteRequest();

    this.httpClient
      .post<QuoteResponse>(`${this.sessionData.apiUrl}/multiInsurance/quote`, request)
      .subscribe(res => {
        this.quoteResponse$.next(res);
        if (res.price > 0) {
          this.quoteStatus = QuoteStatus.Successful;
        } else {
          this.quoteStatus = QuoteStatus.Failed;
        }
      }, err => {
        this.quoteStatus = QuoteStatus.Failed;
        throw new SilentError(err.message);
      });
  }

  private buildQuoteRequest() {
    const request = new QuoteRequest();
    request.correlationId = this.sessionData.correlationId;
    request.trips = this.buildTrips();
    return request;
  }
  
  private buildTrips(): Trip[] {
    const trip = new Trip();
    trip.destination = this.buildDestination();
    trip.travelers = this.buildTravelers();
    trip.tripCost = this.getTripCost();
    trip.startDate = this.getStartDate();
    trip.endDate = this.getEndDate();
    return [trip];
  }
  
  private buildDestination(): string {
    return `${this.reservation.returnLocation.address.stateCode}, ${this.reservation.returnLocation.address.countryCode}`;  
  }

  private buildTravelers(): Traveler[] {
    const traveler = new Traveler();
    traveler.state = this.getDriverInfo().stateCode;
    traveler.tripCost = this.getTripCost()
    return [traveler]
  }
  
  private getTripCost(): number {
    return this.reservation.totalAmount.amount;
  }
  
  private getStartDate(): string {
    return this.formatDateTime(this.reservation.pickUpDate, this.reservation.pickUpTime);
  }
  
  private getEndDate(): string {
    return this.formatDateTime(this.reservation.returnDate, this.reservation.returnTime);
  }
  
  private getDriverInfo(): DriverInformation {
    return this.reservation && this.reservation.driverInformation
  }

  private getDays(): number {
    return this.reservationService.checkPriceData.totalDays + 1;
  }

  private formatDateTime(travelDate: string, travelTime: string): string {

      if (travelTime.length < 4) {
        travelTime = '0' + travelTime;
      }

      const hour = travelTime.substring(0, 2);
      const mins = travelTime.substring(2, 4);
      const date:Date = new Date(travelDate);

      var result = `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()} ${hour}:${mins}:00`;

      return result;
  }
}
